const DOCUMENT_FIELD_INDEX_ATTRIBUTE = 'data-document-field-index';
/** Якорь контрола без записи в `doc.data.fields` (блок вложений). */
const DOCUMENT_ANCHOR_ATTRIBUTE = 'data-document-anchor';

export const highlightDocumentField = (element: HTMLElement): (() => void) => {
    const cleanup = () => {
        cancelAnimationFrame(frame);
        clearTimeout(timer);
        element.classList.remove('document-field--located');
        element.removeEventListener('animationend', onAnimationFinished);
        element.removeEventListener('animationcancel', onAnimationFinished);
    };
    const onAnimationFinished = (event: AnimationEvent) => {
        if (event.target === element && event.animationName.startsWith('completion-field-located')) cleanup();
    };
    element.classList.remove('document-field--located');
    const frame = requestAnimationFrame(() => {
        element.classList.add('document-field--located');
        element.addEventListener('animationend', onAnimationFinished);
        element.addEventListener('animationcancel', onAnimationFinished);
    });
    // Страховка для скрытой/удалённой вкладки и отключённых CSS-анимаций.
    const timer = setTimeout(cleanup, 2800);
    return cleanup;
};

const EDITOR_SELECTOR = [
    // Зона выбора файлов: сам <input type="file"> визуально скрыт, фокусируемая
    // и видимая часть — корень дропзоны (tabindex от react-dropzone).
    '.rf-file-input__dropzone:not([data-disabled])',
    'input:not([type="hidden"]):not([type="file"]):not([disabled]):not([readonly])',
    'textarea:not([disabled]):not([readonly])',
    'select:not([disabled])',
    '[contenteditable="true"]',
    '[role="combobox"]:not([aria-disabled="true"]):not([disabled])',
].join(',');

const findFocusableEditor = (field: HTMLElement): HTMLElement | undefined => {
    const editor = field.querySelector<HTMLElement>(EDITOR_SELECTOR);
    if (!editor || editor.closest('[hidden], [aria-hidden="true"]')) return undefined;
    return editor;
};

/** Раскрыть свёрнутые Box-контейнеры над элементом: у Box нет программного API
 *  раскрытия (collapsed живёт в его useState), поэтому оба пути навигации —
 *  переход к ошибке сохранения и чек-лист заполнения — кликают заголовок. */
export const expandCollapsedAncestors = (element: HTMLElement, root: ParentNode): void => {
    let container: HTMLElement | null = element.parentElement;
    while (container && container !== root) {
        if (container.classList.contains('box-container--collapsed'))
            container.querySelector<HTMLElement>(':scope > .box-title')?.click();
        container = container.parentElement;
    }
};

/** Найти контрол по значению `data-document-anchor`. */
export const findDocumentAnchor = (anchorId: string, root: ParentNode): HTMLElement | undefined =>
    Array.from(root.querySelectorAll<HTMLElement>(`[${DOCUMENT_ANCHOR_ATTRIBUTE}]`)).find(
        (candidate) => candidate.dataset.documentAnchor === anchorId,
    );

/** Фокусирует редактор найденного контрола (если он есть) и прокручивает к нему. */
export const scrollDocumentAnchorIntoView = (element: HTMLElement, root: ParentNode): boolean => {
    expandCollapsedAncestors(element, root);
    // Box раскроется к следующему кадру: scroll/focus по display:none — no-op,
    // поэтому false — пусть retry-цикл вызовет ещё раз после перерендера.
    if (element.closest('.box-container--collapsed')) return false;

    findFocusableEditor(element)?.focus({ preventScroll: true });
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
    return true;
};

/** Прокручивает к первому ошибочному контролу: полю по индексу либо блоку по якорю. */
export const scrollFirstDocumentFieldIntoView = (
    fieldIds: number[],
    root: ParentNode,
    anchorIds: readonly string[] = [],
): boolean => {
    const fieldIdSet = new Set(fieldIds);
    const element =
        Array.from(root.querySelectorAll<HTMLElement>(`[${DOCUMENT_FIELD_INDEX_ATTRIBUTE}]`)).find((candidate) =>
            fieldIdSet.has(Number(candidate.dataset.documentFieldIndex)),
        ) ?? anchorIds.map((anchorId) => findDocumentAnchor(anchorId, root)).find(Boolean);
    if (!element) return false;

    return scrollDocumentAnchorIntoView(element, root);
};
